import React, { useRef, useEffect } from 'react';
import './KIWerkstatt-v2.css';
import KIWerkstattRegistrationForm from '../components/KIWerkstattRegistrationForm';
import RegionalPartnersSection from '../components/RegionalPartnersSection';

const KIWerkstattDeeplearning: React.FC = () => {
  const heroRef = useRef<HTMLElement | null>(null);
  const [preSelectedRegion, setPreSelectedRegion] = React.useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('region') ?? '';
  });

  // If a region query param is present on first load, scroll to the registration form.
  useEffect(() => {
    const region = new URLSearchParams(window.location.search).get('region');
    if (!region) return;
    const timeoutId = window.setTimeout(() => {
      document.getElementById('registration-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, []);

  // Image Gallery Functionality for the "lauffähiges KI-System" section —
  // carousel template (styleguide) with 2 slides paired to 2 benefit tiles.
  useEffect(() => {
    const setupGallery = (sectionSelector: string) => {
      const section = document.querySelector(sectionSelector);
      if (!section) return null;

      let currentSlide = 0;
      const slides = section.querySelectorAll('.gallery-slide');
      const benefitItems = section.querySelectorAll('.benefit-item');

      if (slides.length === 0) return null;

      const showSlide = (index: number) => {
        slides.forEach(slide => slide.classList.remove('active'));
        benefitItems.forEach(item => item.classList.remove('active'));

        slides[index]?.classList.add('active');
        benefitItems[index]?.classList.add('active');
        currentSlide = index;
      };

      const nextSlide = () => {
        currentSlide = (currentSlide + 1) % slides.length;
        showSlide(currentSlide);
      };

      let autoplayInterval = setInterval(nextSlide, 4000);

      const benefitClickHandlers: (() => void)[] = [];
      const benefitHoverHandlers: (() => void)[] = [];
      benefitItems.forEach((item, index) => {
        const handler = () => {
          showSlide(index);
          clearInterval(autoplayInterval);
          autoplayInterval = setInterval(nextSlide, 4000);
        };
        benefitClickHandlers.push(handler);
        benefitHoverHandlers.push(handler);
        item.addEventListener('click', handler);
        item.addEventListener('mouseenter', handler);
      });

      benefitItems[0]?.classList.add('active');

      return () => {
        clearInterval(autoplayInterval);
        benefitItems.forEach((item, index) => {
          item.removeEventListener('click', benefitClickHandlers[index]);
          item.removeEventListener('mouseenter', benefitHoverHandlers[index]);
        });
      };
    };

    const cleanup = setupGallery('[data-section="ki-werkstatt-produktion-overview"]');

    return () => {
      cleanup?.();
    };
  }, []);

  // Ensure hero overlaps header on mobile exactly like page-header
  useEffect(() => {
    const applyMobileHeroOffset = () => {
      if (!heroRef.current) return;
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        heroRef.current.style.marginTop = '';
        heroRef.current.style.paddingTop = '';
        return;
      }
      const header = document.querySelector<HTMLElement>('.header-container');
      const headerHeight = header ? header.offsetHeight : 70;
      heroRef.current.style.marginTop = `-${headerHeight}px`;
      const baseTopPaddingRem = 2;
      const baseTopPaddingPx = baseTopPaddingRem * 16;
      const computedTopPadding = headerHeight + baseTopPaddingPx;
      heroRef.current.style.paddingTop = `${computedTopPadding}px`;
    };

    applyMobileHeroOffset();
    window.addEventListener('resize', applyMobileHeroOffset, { passive: true });
    window.addEventListener('orientationchange', applyMobileHeroOffset, { passive: true } as any);
    return () => {
      window.removeEventListener('resize', applyMobileHeroOffset as any);
      window.removeEventListener('orientationchange', applyMobileHeroOffset as any);
    };
  }, []);

  return (
    <div className="page ki-werkstatt-page ki-werkstatt-produktion-page">
      {/* Hero Section */}
      <section
        ref={(el) => { heroRef.current = el; }}
        className="page-header ki-werkstatt-hero"
        data-section="ki-werkstatt-produktion-hero"
      >
        <div className="container">
          <div className="ki-werkstatt-hero-content">
            <span className="hero-pretitle">Ein Workshop-Tag in der</span>
            <h1 className="ki-werkstatt-hero-title">KI-Werkstatt</h1>
            <div className="hero-separator"></div>
            <p className="ki-werkstatt-hero-subtitle">
              KI-Entwicklung für die Fertigung
            </p>
          </div>
        </div>
      </section>

      {/* Section 1: Overview */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-produktion-overview">
        <div className="container">
          <h2 className="section-title">Ein <span className="highlight-text">lauffähiges KI-System</span> an einem Tag</h2>
          <p className="section-subtitle">
            VON DER KONZEPTION BIS ZUM FUNKTIONSFÄHIGEN PROTOTYP
          </p>
          <div className="ki-werkstatt-overlay-layout">
            <div className="overlay-gallery">
              <div className="gallery-container">
                <div className="gallery-slides">
                  <div className="gallery-slide active">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_hardware.webp" type="image/webp" />
                      <img
                        src="/assets/images/ki-werkstatt/kiwerkstatt_hardware.jpeg"
                        alt="Nachbau eines industriellen KI-Systems in der KI-Werkstatt"
                        width="1200"
                        height="900"
                        loading="eager"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                  <div className="gallery-slide">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_knowhow.webp" type="image/webp" />
                      <img
                        src="/assets/images/ki-werkstatt/kiwerkstatt_knowhow.jpeg"
                        alt="Fachliche Begleitung durch das mintkitchen-Team"
                        width="1200"
                        height="900"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                </div>
              </div>
            </div>
            <div className="overlay-benefits">
              <div className="benefits-list">
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Lernen durch Nachbauen</h4>
                    <p className="benefit-description">
                      Das Nachbauen existierender KI-Systeme mit fachlicher Begleitung ist ein idealer Weg, einschlägiges Know-How im Bereich industrieller KI-Entwicklung aufzubauen. Im Tagesworkshop bauen wir in einer kleinen Gruppe ein seit Jahren laufendes KI-System aus der Industrie nach.
                    </p>
                  </div>
                </div>
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Startklar nach einem Workshop-Tag</h4>
                    <p className="benefit-description">
                      Gemeinsam durchlaufen wir systematisch alle Schritte der KI-Entwicklung. Du erhältst einen vollständigen Einblick in zentrale technische Aspekte und Best Practices. In der KI-Werkstatt erhältst Du einen klaren Fahrplan und das notwendige Handwerkszeug für Deine eigenen KI-Projekte.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Reference System */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-produktion-reference">
        <div className="container">
          <h2 className="section-title">Das <span className="highlight-text">Referenz-System</span> im Workshop</h2>
          <p className="section-subtitle">
            Nachbau des KI-Sicherheitssystems HANDGUARD der Firma <a href="https://www.altendorf.com/" target="_blank" rel="noopener noreferrer" className="highlight-text">Altendorf</a>
          </p>
          <div className="reference-two-column-layout">
            <div className="reference-column" lang="de">
              <p>
                Als Referenzsystem für den Einstiegs-Workshop dient das KI-gestützte Sicherheitssystem <a href="https://www.altendorf.com/machines/formatkreissaege-altendorf-hand-guard/" target="_blank" rel="noopener noreferrer" className="highlight-text">HANDGUARD</a>{' '}
                der Firma <a href="https://www.altendorf.com/" target="_blank" rel="noopener noreferrer" className="highlight-text">Altendorf</a>. Dieses Sicherheitssystem nutzt Computer Vision und AI-Edge-Computing, um 
                Gefahrensituationen im Arbeitsbereich von Formatkreissägen visuell zu erkennen und Arbeitsunfälle 
                präventiv durch das Herabsenken des Sägeblatts zu verhindern.
              </p>
            </div>
            <div className="reference-column" lang="de">
              <p>
                Das mehrfach ausgezeichnete KI-Sicherheitssystem ist ein Leuchtturm für den industriellen KI-Einsatz 
                durch einen traditionellen Maschinenbauer. Im Tagesworkshop replizieren wir dieses KI-Sicherheitssystem 
                Schritt für Schritt auf vergleichbarer Hard- und Software.
              </p>
            </div>
          </div>
          <div className="video-container">
            <iframe
              className="youtube-video"
              src="https://www.youtube.com/embed/nIbTTiTfHn8"
              title="Altendorf HANDGUARD KI-Sicherheitssystem"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Section 3: Schedule */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-produktion-content">
        <div className="container">
          <h2 className="section-title">Der <span className="highlight-text">Ablauf</span> Deines Workshop-Tags</h2>
          
          <div className="workshop-schedule">
            <div className="schedule-item">
              <div className="schedule-time">8:00 - 10:00</div>
              <div className="schedule-content">
              <h3>Theorie & Konzeption</h3>
              <p>
                Technische Grundlagen von Deep Learning, Transfer Learning und AI-Edge-Computing. 
                Abgrenzung zwischen KI-basierter und konventioneller Automation. 
                Technische Konzeption des KI-Systems für die Arbeitssicherheit.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">10:00 - 12:00</div>
              <div className="schedule-content">
              <h3>Datenerhebung & Labelling</h3>
              <p>
                Gemeinsame Erhebung und Markierung von Trainingsdaten für das KI-System. 
                Strategien, Software und Methoden für Datenerhebung und Markierung im industriellen Maßstab.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">12:00 - 13:00</div>
              <div className="schedule-content">
              <h3>Gemeinsames Mittagessen & Netzwerken</h3>
              <p>
                Gemeinsames Mittagessen vor Ort. Vernetzung und Austausch zwischen Teilnehmern und Dozenten.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">13:00 - 15:00</div>
              <div className="schedule-content">
              <h3>KI-Training und Live-Tests</h3>
              <p>
                Lokales KI-Training und Deployment auf der Nvidia Jetson Platform. 
                Systematische Tests am Echtzeit-Prototypen des trainierten KI-Systems. 
                Evaluation der Performance, Robustheit, Skalierbarkeit und Wartbarkeit.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">15:00 - 17:00</div>
              <div className="schedule-content">
              <h3>Konzeption eigener KI-Projekte</h3>
              <p>
                Übertragung von Gelerntem auf Anwendungsfelder der Teilnehmer. 
                Gemeinsame Diskussion und fachliche Beurteilung eigener KI-Anwendungsideen. 
                Ausarbeitung grober Roadmaps und technische Architekturen für die eigenen KI-Anwendungsideen.
              </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Regional Partners Map */}
      <RegionalPartnersSection onRegionSelect={setPreSelectedRegion} variant="produktion" />

      {/* Section 5: Workshop Details */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-produktion-details">
        <div className="container">
          <h2 className="section-title">Workshop <span className="highlight-text">Details</span></h2>
          
          <div className="workshop-grid">
            <div className="workshop-column">
              <h3>Zielgruppe</h3>
              <p>Technische Fachkräfte, Produktionsverantwortliche, KI-Interessierte aus der Industrie</p>
            </div>
            
            <div className="workshop-column">
              <h3>Voraussetzungen</h3>
              <p>Technisches Interesse. Programmierkenntnisse sind hilfreich, aber nicht erforderlich.</p>
            </div>
            
            <div className="workshop-column">
              <h3>Dauer</h3>
              <p>1 Tageskurs<br />08:00 - 17:00 Uhr</p>
            </div>
            
            <div className="workshop-column">
              <h3>Bereitgestellte Hardware</h3>
              <p>Nvidia Jetson für Training und Inferenz, Industriekameras, Aktuatoren</p>
            </div>
            
            <div className="workshop-column">
              <h3>Bereitgestellte Software</h3>
              <p>Vorkonfiguriertes Nvidia Jetpack & Jetson Inference, ML-Software TeachSense</p>
            </div>
            
            <div className="workshop-column">
              <h3>Teilnahmegebühr</h3>
              <p><span className="highlight-text">990 €</span> zzgl. USt. pro Person.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Registration */}
      <section id="registration-section" className="ki-werkstatt-section" data-section="ki-werkstatt-produktion-registration">
        <div className="container">
          <h2 className="section-title"><span className="highlight-text">Unverbindliche</span> Reservierung</h2>
          <p className="section-subtitle">
            DATUM WIRD MIT INTERESSIERTEN ABGESTIMMT
          </p>
          <KIWerkstattRegistrationForm variant="produktion" initialRegion={preSelectedRegion} />
        </div>
      </section>

      {/* Section 7: Cross-link to other variant */}
      <section className="ki-werkstatt-section variant-alternative" data-section="ki-werkstatt-produktion-alternative" style={{ background: '#008F99' }}>
        <div className="container">
          <h3 style={{ color: 'white' }}>Interessiert an KI-Automation in der Verwaltung?</h3>
          <p style={{ color: 'white' }}>Entdecke souveräne KI-Nutzung durch lokal betriebene RAG-Chatbots</p>
          <a href="/ki-werkstatt/verwaltung" className="btn btn-secondary-light">
            Zur KI-Werkstatt "Verwaltung" →
          </a>
        </div>
      </section>
    </div>
  );
};

export default KIWerkstattDeeplearning;
